/**
 * RBAC — the platform's single source of truth for roles, actions and
 * permission checks. Pure JS, zero React, zero I/O, so it is trivially
 * testable (see `verify-rbac.mjs`) and importable from any module.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * SECURITY BOUNDARY — READ THIS BEFORE TRUSTING ANYTHING HERE.
 *
 * Everything in this file is **UX only**. It decides what a browser draws:
 * which nav items appear, which buttons enable, which rows offer "take
 * control". It is NOT access control. The bundle ships to the client, the
 * role is read from a payload the client already holds, and any user can
 * edit both in devtools.
 *
 * REAL enforcement lives in sr-portal-backend. HALF of it now exists: the role
 * (and a rep's book) IS on the verified JWT, and the roster routes re-check it
 * with `requireExec`. The other half does not: the data and write routes still
 * authorize on "has a valid token" alone, so a determined user can still call
 * them directly for an account outside their book. See RBAC.md
 * ("Backend work required" → item 2) for the exact endpoints and checks.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ---- Roles ---------------------------------------------------------- */

export const ROLES = {
  EXEC: 'exec',
  REP: 'rep',
  OPS: 'ops',
  MARKETING: 'marketing',
  /**
   * No role on the session. This was the state of every real user before the
   * backend started assigning roles; it now means the caller is not on the team
   * roster, is deactivated, or has not been given a role yet. See POLICY below
   * for why it is deliberately permissive on reads and hard-denied on control.
   */
  UNSCOPED: 'unscoped',
}

export const ROLE_ORDER = [ROLES.EXEC, ROLES.REP, ROLES.OPS, ROLES.MARKETING, ROLES.UNSCOPED]

export const ROLE_META = {
  [ROLES.EXEC]: {
    label: 'Executive',
    lane: 'All lanes',
    blurb: 'Full control of any account, every module.',
  },
  [ROLES.REP]: {
    label: 'Sales rep',
    lane: 'Own book',
    blurb: 'Control limited to their own assigned accounts.',
  },
  [ROLES.OPS]: {
    label: 'Operations',
    lane: 'Production & fulfillment',
    blurb: 'Helps the team move orders through production and delivery.',
  },
  [ROLES.MARKETING]: {
    label: 'Marketing',
    lane: 'Customer-facing',
    blurb: 'Digital assets, campaigns and customer-facing materials.',
  },
  [ROLES.UNSCOPED]: {
    label: 'No role assigned',
    lane: 'Read-only lane',
    blurb: 'The session carries no role yet — account control is disabled.',
  },
}

/**
 * Aliases the backend (or HubSpot) might hand us. Anything unrecognised falls
 * to UNSCOPED — we never guess a user into more power than we can read.
 */
const ROLE_ALIASES = new Map(
  Object.entries({
    exec: ROLES.EXEC,
    executive: ROLES.EXEC,
    admin: ROLES.EXEC,
    administrator: ROLES.EXEC,
    owner: ROLES.EXEC,
    superuser: ROLES.EXEC,

    rep: ROLES.REP,
    sales: ROLES.REP,
    salesrep: ROLES.REP,
    'sales rep': ROLES.REP,
    representative: ROLES.REP,
    ae: ROLES.REP,

    ops: ROLES.OPS,
    operations: ROLES.OPS,
    production: ROLES.OPS,
    fulfillment: ROLES.OPS,
    fulfilment: ROLES.OPS,

    marketing: ROLES.MARKETING,
    mktg: ROLES.MARKETING,
    brand: ROLES.MARKETING,
    creative: ROLES.MARKETING,
  })
)

/** Coerce any DTO flavour (string / array / object) into one canonical role. */
export function normalizeRole(raw) {
  if (!raw) return ROLES.UNSCOPED
  if (Array.isArray(raw)) {
    // Multiple roles: take the most privileged one we recognise.
    const found = raw.map(normalizeRole).filter((r) => r !== ROLES.UNSCOPED)
    if (!found.length) return ROLES.UNSCOPED
    return ROLE_ORDER.find((r) => found.includes(r)) || ROLES.UNSCOPED
  }
  if (typeof raw === 'object') {
    return normalizeRole(raw.role ?? raw.name ?? raw.slug ?? raw.value ?? null)
  }
  const key = String(raw).trim().toLowerCase()
  if (!key) return ROLES.UNSCOPED
  return ROLE_ALIASES.get(key) || ROLES.UNSCOPED
}

/**
 * Pull the role off whatever the session gives us. sr-portal-backend now sends
 * `role` on both the JWT and /api/access/me, so the first candidate key is the
 * live one; the rest are tolerated flavours. If none is present we land on
 * UNSCOPED — we never guess.
 */
export function roleFromUser(user) {
  if (!user || typeof user !== 'object') return ROLES.UNSCOPED
  return normalizeRole(
    user.role ?? user.sr_role ?? user.roles ?? user.userRole ?? user.scope ?? null
  )
}

/* ---- Actions -------------------------------------------------------- */

/**
 * Every gated capability in the platform, one id per action. Modules import
 * these constants — never a bare string — so a typo is a build-time reference
 * error rather than a silently-denied (or silently-allowed) check.
 *
 * `kind`:
 *   read    — surfacing data
 *   write   — mutates a record (these are what the audit trail records)
 *   control — takes control of an account, or changes who has power
 */
export const ACTIONS = {
  /* Platform */
  ACTING_START: 'acting.start',
  AUDIT_READ: 'audit.read',
  /**
   * Manage the internal team roster: add/remove personnel, set someone's role,
   * assign a rep's book. This is the action that DECIDES every other action, so
   * it is exec-only and the server re-checks it (`requireExec` on
   * /api/admin/team*) rather than trusting this file.
   */
  TEAM_MANAGE: 'team.manage',

  /* Command Center */
  CC_VIEW: 'cc.view',
  CC_CHAMPION_CROWN: 'cc.champion.crown',
  CC_CHAMPION_UPDATE: 'cc.champion.update',
  CC_CHAMPION_FIRE_ARTWORK: 'cc.champion.fireArtwork',
  /**
   * MANAGE ASSIGNMENTS — approve an engine proposal, pick a rep on a record, or
   * bulk-reassign a selection. It writes `sr_assigned_rep`, which is who OWNS
   * the opportunity, so it is `control` rather than `write` and is exec-only in
   * the matrix. `lib/assignmentPermissions.js` adds the exec-only toggle that
   * grants it to one person or one role without changing their role.
   */
  CC_REP_ASSIGN: 'cc.rep.assign',

  /* Production (the module renamed from "Customer Journey" 2026-09-11).
   *
   * ⚠️ THE ACTION KEYS STAY `journey.*` ON PURPOSE. They are on the JWT, in
   * sr-portal-backend's permissions map and in verify-rbac.mjs's 207
   * assertions. Renaming a wire value to match a UI label would log every
   * holder of the old claim out of the module the moment the frontend shipped
   * ahead of the backend — a cosmetic edit with an outage attached. The label
   * is what a person reads; the key is what two systems agree on. */
  JOURNEY_VIEW: 'journey.view',
  JOURNEY_ACCOUNT_CONTROL: 'journey.account.control',
  JOURNEY_APPROVAL_ADVANCE: 'journey.approval.advance',
  JOURNEY_PRODUCTION_UPDATE: 'journey.production.update',
  JOURNEY_DELIVERY_UPDATE: 'journey.delivery.update',
  JOURNEY_ORDER_HOLD: 'journey.order.hold',

  /* Artwork Studio */
  ARTWORK_VIEW: 'artwork.view',
  ARTWORK_BUILD_CREATE: 'artwork.build.create',
  ARTWORK_BUILD_SUBMIT: 'artwork.build.submit',
  ARTWORK_MARKS_PROPOSE: 'artwork.marks.propose',
  ARTWORK_MARKS_APPROVE: 'artwork.marks.approve',

  /* Reviews — its own module, deliberately NOT under Pricing.
   *
   * Brian: "Reviews should have its own portal... it shouldn't be associated
   * with solely finance." The permission is separate for the same reason the
   * module is: a marketing lane needs the content shelf without ever seeing a
   * quote, and reviews are triggered by DELIVERY rather than by money. */
  REVIEWS_VIEW: 'reviews.view',
  /** Record that an ask happened. The backend refuses it before delivery. */
  REVIEWS_ASK: 'reviews.ask',
  /** Confirm a public review really landed. Never the rep who asked. */
  REVIEWS_VERIFY: 'reviews.verify',
  /** Approve a customer's words for publication, and flag do-not-ask. */
  REVIEWS_PUBLISH: 'reviews.publish',

  /* Pricing */
  PRICING_VIEW: 'pricing.view',
  PRICING_QUOTE_CREATE: 'pricing.quote.create',
  PRICING_DISCOUNT_OVERRIDE: 'pricing.discount.override',

  /* Product Builders */
  BUILDERS_VIEW: 'builders.view',
  BUILDERS_CONFIGURE: 'builders.configure',

  /**
   * Presentations. The hub holds NO presentation data — the module is a
   * launcher into the standalone, password-gated portal at present.srchamp.com,
   * which does its own gating per film. So this is a "may you see the link"
   * read, nothing more; the portal is the thing that actually authorizes.
   */
  PRESENTATIONS_VIEW: 'presentations.view',
  /* The launcher into team.srchamp.com — the customer team meeting rooms.
   * A READ on a link surface, not on customer data: the portal authorises
   * every one of its own calls server-side and a coach's room is reachable
   * only with a credential SR issued. Hiding the link from staff would buy
   * nothing, exactly as with PRESENTATIONS_VIEW. */
  TEAMROOMS_VIEW: 'teamrooms.view',

  /**
   * Top Tier Teams. Like Presentations, the module holds NO data of its own —
   * it is a curated launcher into surfaces that already exist and already
   * authorize their own users. So this is a "may you see the directory" read.
   */
  TOPTIER_VIEW: 'toptier.view',
  /**
   * See the INTERNAL ring pricing / publish / tracking console link on a Top
   * Tier card. Split from TOPTIER_VIEW so the customer-facing links and the
   * staff console can move apart later without touching a component: the
   * portal and the film are things we send to a customer, the console is where
   * ring pricing gets published and views get tracked.
   *
   * Still a read of a LINK, not of pricing — the console has its own sign-in
   * and is the thing that actually authorizes. Hiding the link is navigation
   * hygiene, exactly as documented at the top of this file.
   */
  TOPTIER_ADMIN_VIEW: 'toptier.admin.view',

  /**
   * AI Tools. The same launcher shape as Presentations and Top Tier: the hub
   * holds NO tool of its own — every entry is a Custom GPT hosted on ChatGPT,
   * which authorises its own user with their own ChatGPT sign-in.
   *
   * So this is emphatically a "may you see the list" read. Hiding it would buy
   * nothing, because a person without a ChatGPT account that has been granted
   * the GPT cannot open it whether they can see the link or not — and a person
   * who HAS one can reach it from chatgpt.com without us.
   */
  AITOOLS_VIEW: 'aitools.view',

  /**
   * RUN A SYSTEM JOB — the champion sweep, the hunt, a harvest, a load.
   *
   * ⚠️ THIS MIRRORS THE BACKEND, IT DOES NOT DEFINE IT. sr-portal-backend's
   * own `rbac.js` owns `system.run` and grants it to **exec and ops only**;
   * `permissions.js` maps `POST /api/champion-sweep` (and champion-hunt,
   * backtest/run, the harvests and loaders) to it and re-decides server-side.
   *
   * Its comment there says SYSTEM_RUN "has no client equivalent and is
   * intentionally server-only", which was right while nothing in this app
   * called those routes. It does now, and a button has to know whether to draw
   * itself. So this exists for exactly that — a DRAWING decision, matching the
   * server's grant exactly so the two cannot disagree about who sees it. The
   * server is still the thing that refuses.
   *
   * `control`, not `read`: it starts work on the whole universe.
   */
  SYSTEM_RUN: 'system.run',

  /* Campaigns / customer-facing materials (marketing lane) */
  CAMPAIGN_SEND: 'campaign.send',
  CAMPAIGN_ASSET_MANAGE: 'campaign.asset.manage',
}

export const ACTION_META = {
  [ACTIONS.ACTING_START]: { kind: 'control', label: 'Take control of an account' },
  [ACTIONS.AUDIT_READ]: { kind: 'read', label: 'Read the audit trail' },
  [ACTIONS.TEAM_MANAGE]: { kind: 'control', label: 'Manage the team roster' },

  [ACTIONS.CC_VIEW]: { kind: 'read', label: 'View Command Center' },
  [ACTIONS.CC_CHAMPION_CROWN]: { kind: 'write', label: 'Record a champion' },
  [ACTIONS.CC_CHAMPION_UPDATE]: { kind: 'write', label: 'Correct championship fields' },
  [ACTIONS.CC_CHAMPION_FIRE_ARTWORK]: { kind: 'write', label: 'Fire ring artwork' },
  [ACTIONS.CC_REP_ASSIGN]: { kind: 'control', label: 'Manage Assignments' },

  [ACTIONS.JOURNEY_VIEW]: { kind: 'read', label: 'View Production' },
  [ACTIONS.JOURNEY_ACCOUNT_CONTROL]: { kind: 'control', label: 'Control this account' },
  [ACTIONS.JOURNEY_APPROVAL_ADVANCE]: { kind: 'write', label: 'Advance an approval group' },
  [ACTIONS.JOURNEY_PRODUCTION_UPDATE]: { kind: 'write', label: 'Update production stage' },
  [ACTIONS.JOURNEY_DELIVERY_UPDATE]: { kind: 'write', label: 'Update delivery' },
  [ACTIONS.JOURNEY_ORDER_HOLD]: { kind: 'write', label: 'Hold / release an order' },

  [ACTIONS.ARTWORK_VIEW]: { kind: 'read', label: 'View Artwork Studio' },
  [ACTIONS.ARTWORK_BUILD_CREATE]: { kind: 'write', label: 'Create an artwork build' },
  [ACTIONS.ARTWORK_BUILD_SUBMIT]: { kind: 'write', label: 'Submit a build' },
  [ACTIONS.ARTWORK_MARKS_PROPOSE]: { kind: 'write', label: 'Propose a mark' },
  [ACTIONS.ARTWORK_MARKS_APPROVE]: { kind: 'write', label: 'Approve / reject a mark' },

  [ACTIONS.REVIEWS_VIEW]: { kind: 'read', label: 'View Reviews' },
  [ACTIONS.REVIEWS_ASK]: { kind: 'write', label: 'Record a review ask' },
  [ACTIONS.REVIEWS_VERIFY]: { kind: 'write', label: 'Confirm a review landed' },
  [ACTIONS.REVIEWS_PUBLISH]: { kind: 'control', label: 'Approve a testimonial for use' },

  [ACTIONS.PRICING_VIEW]: { kind: 'read', label: 'View Pricing' },
  [ACTIONS.PRICING_QUOTE_CREATE]: { kind: 'write', label: 'Create a quote' },
  [ACTIONS.PRICING_DISCOUNT_OVERRIDE]: { kind: 'write', label: 'Override discount floor' },

  [ACTIONS.BUILDERS_VIEW]: { kind: 'read', label: 'View Product Builders' },
  [ACTIONS.BUILDERS_CONFIGURE]: { kind: 'write', label: 'Configure a product' },

  [ACTIONS.PRESENTATIONS_VIEW]: { kind: 'read', label: 'View Presentations' },
  [ACTIONS.TEAMROOMS_VIEW]: { kind: 'read', label: 'View Team Meeting Rooms' },

  [ACTIONS.TOPTIER_VIEW]: { kind: 'read', label: 'View Top Tier Teams' },
  [ACTIONS.AITOOLS_VIEW]: { kind: 'read', label: 'View AI Tools' },
  [ACTIONS.SYSTEM_RUN]: { kind: 'control', label: 'Run a system job' },
  [ACTIONS.TOPTIER_ADMIN_VIEW]: { kind: 'read', label: 'Open the internal ring console' },

  [ACTIONS.CAMPAIGN_SEND]: { kind: 'write', label: 'Send a campaign' },
  [ACTIONS.CAMPAIGN_ASSET_MANAGE]: { kind: 'write', label: 'Manage customer-facing assets' },
}

export const ALL_ACTIONS = Object.values(ACTIONS)

/** Actions whose success must land in the audit trail. */
export function isWriteAction(action) {
  const kind = ACTION_META[action]?.kind
  return kind === 'write' || kind === 'control'
}

/* ---- The policy matrix ---------------------------------------------- */

const ANY = true // allowed for every account
const OWN = 'own' // allowed only for accounts in the actor's book

/**
 * role → { action: ANY | OWN }. An action missing from a role's map is DENIED.
 *
 * UNSCOPED deserves a word. Today the live app has no roles: every signed-in
 * user can see every module and issue the writes that already exist. If an
 * unknown role defaulted to "deny everything", shipping this foundation would
 * black out the app for every real user — a regression dressed up as security.
 * So UNSCOPED is pinned to *exactly today's* capability set: read everything,
 * keep the writes that already shipped, and get NOTHING new. Account control —
 * the genuinely dangerous new power — fails closed for it.
 *
 * The backend now puts a real role on the session, so users move off UNSCOPED
 * and the tighter lanes below take effect with no client change. UNSCOPED
 * remains the landing spot for anyone not on the roster or not yet assigned.
 */
export const POLICY = {
  [ROLES.EXEC]: {
    [ACTIONS.ACTING_START]: ANY,
    [ACTIONS.AUDIT_READ]: ANY,
    [ACTIONS.TEAM_MANAGE]: ANY,
    [ACTIONS.CC_VIEW]: ANY,
    [ACTIONS.CC_CHAMPION_CROWN]: ANY,
    [ACTIONS.CC_CHAMPION_UPDATE]: ANY,
    [ACTIONS.CC_CHAMPION_FIRE_ARTWORK]: ANY,
    [ACTIONS.CC_REP_ASSIGN]: ANY,
    [ACTIONS.JOURNEY_VIEW]: ANY,
    [ACTIONS.JOURNEY_ACCOUNT_CONTROL]: ANY,
    [ACTIONS.JOURNEY_APPROVAL_ADVANCE]: ANY,
    [ACTIONS.JOURNEY_PRODUCTION_UPDATE]: ANY,
    [ACTIONS.JOURNEY_DELIVERY_UPDATE]: ANY,
    [ACTIONS.JOURNEY_ORDER_HOLD]: ANY,
    [ACTIONS.ARTWORK_VIEW]: ANY,
    [ACTIONS.ARTWORK_BUILD_CREATE]: ANY,
    [ACTIONS.ARTWORK_BUILD_SUBMIT]: ANY,
    [ACTIONS.ARTWORK_MARKS_PROPOSE]: ANY,
    [ACTIONS.ARTWORK_MARKS_APPROVE]: ANY,
    [ACTIONS.REVIEWS_VIEW]: ANY,
    [ACTIONS.REVIEWS_ASK]: ANY,
    [ACTIONS.REVIEWS_VERIFY]: ANY,
    [ACTIONS.REVIEWS_PUBLISH]: ANY,
    [ACTIONS.PRICING_VIEW]: ANY,
    [ACTIONS.PRICING_QUOTE_CREATE]: ANY,
    [ACTIONS.PRICING_DISCOUNT_OVERRIDE]: ANY,
    [ACTIONS.BUILDERS_VIEW]: ANY,
    [ACTIONS.BUILDERS_CONFIGURE]: ANY,
    [ACTIONS.CAMPAIGN_SEND]: ANY,
    [ACTIONS.CAMPAIGN_ASSET_MANAGE]: ANY,
    [ACTIONS.PRESENTATIONS_VIEW]: ANY,
    [ACTIONS.TEAMROOMS_VIEW]: ANY,
    [ACTIONS.AITOOLS_VIEW]: ANY,
    /* Mirrors sr-portal-backend: exec and ops only. */
    [ACTIONS.SYSTEM_RUN]: ANY,
    [ACTIONS.TOPTIER_VIEW]: ANY,
    [ACTIONS.TOPTIER_ADMIN_VIEW]: ANY,
  },

  // Sales: full power, but only over the accounts assigned to them.
  [ROLES.REP]: {
    [ACTIONS.ACTING_START]: OWN,
    [ACTIONS.CC_VIEW]: ANY,
    [ACTIONS.CC_CHAMPION_CROWN]: OWN,
    [ACTIONS.CC_CHAMPION_UPDATE]: OWN,
    [ACTIONS.JOURNEY_VIEW]: ANY,
    [ACTIONS.JOURNEY_ACCOUNT_CONTROL]: OWN,
    [ACTIONS.JOURNEY_APPROVAL_ADVANCE]: OWN,
    [ACTIONS.JOURNEY_ORDER_HOLD]: OWN,
    [ACTIONS.ARTWORK_VIEW]: ANY,
    [ACTIONS.ARTWORK_BUILD_CREATE]: OWN,
    [ACTIONS.ARTWORK_BUILD_SUBMIT]: OWN,
    [ACTIONS.ARTWORK_MARKS_PROPOSE]: ANY,
    // Reps ask for reviews on their own book. They deliberately CANNOT verify
    // that a public one landed — the whole value of "verified" is that a
    // second person opened the link.
    [ACTIONS.REVIEWS_VIEW]: ANY,
    [ACTIONS.REVIEWS_ASK]: OWN,
    [ACTIONS.PRICING_VIEW]: ANY,
    [ACTIONS.PRICING_QUOTE_CREATE]: OWN,
    [ACTIONS.BUILDERS_VIEW]: ANY,
    [ACTIONS.BUILDERS_CONFIGURE]: ANY,
    [ACTIONS.PRESENTATIONS_VIEW]: ANY,
    [ACTIONS.TEAMROOMS_VIEW]: ANY,
    [ACTIONS.AITOOLS_VIEW]: ANY,
    // Reps see the account directory and both customer-facing links — those are
    // exactly what they send. Publishing ring pricing is not a sales action, so
    // TOPTIER_ADMIN_VIEW is withheld.
    [ACTIONS.TOPTIER_VIEW]: ANY,
  },

  // Operations: help the team move orders through production → delivery.
  // Not a sales lane — no crowning, no quoting, no campaigns.
  [ROLES.OPS]: {
    [ACTIONS.ACTING_START]: ANY,
    [ACTIONS.CC_VIEW]: ANY,
    [ACTIONS.JOURNEY_VIEW]: ANY,
    [ACTIONS.JOURNEY_ACCOUNT_CONTROL]: ANY,
    [ACTIONS.JOURNEY_PRODUCTION_UPDATE]: ANY,
    [ACTIONS.JOURNEY_DELIVERY_UPDATE]: ANY,
    [ACTIONS.JOURNEY_ORDER_HOLD]: ANY,
    [ACTIONS.ARTWORK_VIEW]: ANY,
    [ACTIONS.ARTWORK_BUILD_SUBMIT]: ANY,
    // Ops owns delivery, which is the trigger for the whole queue — they are
    // the right people to confirm a public review really landed, and they are
    // never the person who asked for it.
    [ACTIONS.REVIEWS_VIEW]: ANY,
    [ACTIONS.REVIEWS_VERIFY]: ANY,
    [ACTIONS.PRICING_VIEW]: ANY,
    [ACTIONS.BUILDERS_VIEW]: ANY,
    [ACTIONS.PRESENTATIONS_VIEW]: ANY,
    [ACTIONS.TEAMROOMS_VIEW]: ANY,
    [ACTIONS.AITOOLS_VIEW]: ANY,
    /* Mirrors sr-portal-backend: exec and ops only. */
    [ACTIONS.SYSTEM_RUN]: ANY,
    // Ops runs the publish/tracking side of a live ring program, so the
    // internal console is in-lane for them.
    [ACTIONS.TOPTIER_VIEW]: ANY,
    [ACTIONS.TOPTIER_ADMIN_VIEW]: ANY,
  },

  // Marketing: the customer-facing side — assets, marks, campaigns.
  // Reads the pipeline, never advances production or records a winner.
  [ROLES.MARKETING]: {
    [ACTIONS.ACTING_START]: ANY,
    [ACTIONS.CC_VIEW]: ANY,
    [ACTIONS.JOURNEY_VIEW]: ANY,
    [ACTIONS.JOURNEY_ACCOUNT_CONTROL]: ANY,
    [ACTIONS.ARTWORK_VIEW]: ANY,
    [ACTIONS.ARTWORK_BUILD_CREATE]: ANY,
    [ACTIONS.ARTWORK_MARKS_PROPOSE]: ANY,
    [ACTIONS.ARTWORK_MARKS_APPROVE]: ANY,
    // THE CONTENT SHELF IS A MARKETING SURFACE. An internal testimonial is an
    // asset SR owns and publishes, which is this lane's whole job — and the
    // reason reviews could not live inside a finance module.
    [ACTIONS.REVIEWS_VIEW]: ANY,
    [ACTIONS.REVIEWS_VERIFY]: ANY,
    [ACTIONS.REVIEWS_PUBLISH]: ANY,
    [ACTIONS.PRICING_VIEW]: ANY,
    [ACTIONS.BUILDERS_VIEW]: ANY,
    [ACTIONS.BUILDERS_CONFIGURE]: ANY,
    [ACTIONS.CAMPAIGN_SEND]: ANY,
    [ACTIONS.CAMPAIGN_ASSET_MANAGE]: ANY,
    [ACTIONS.PRESENTATIONS_VIEW]: ANY,
    [ACTIONS.TEAMROOMS_VIEW]: ANY,
    [ACTIONS.AITOOLS_VIEW]: ANY,
    [ACTIONS.TOPTIER_VIEW]: ANY,
  },

  // No role on the session — today's app, exactly. Reads + the writes that
  // already shipped. No acting-as, no audit, no new privilege.
  [ROLES.UNSCOPED]: {
    [ACTIONS.CC_VIEW]: ANY,
    [ACTIONS.CC_CHAMPION_CROWN]: ANY,
    [ACTIONS.CC_CHAMPION_UPDATE]: ANY,
    [ACTIONS.JOURNEY_VIEW]: ANY,
    [ACTIONS.ARTWORK_VIEW]: ANY,
    [ACTIONS.ARTWORK_BUILD_CREATE]: ANY,
    [ACTIONS.ARTWORK_BUILD_SUBMIT]: ANY,
    [ACTIONS.ARTWORK_MARKS_PROPOSE]: ANY,
    [ACTIONS.ARTWORK_MARKS_APPROVE]: ANY,
    // Reviews are NEW, so an unscoped session gets the read and nothing else.
    // The permissive UNSCOPED lane exists to avoid regressing capability that
    // already shipped; a module built today has none to regress, and the ask
    // is the one action where a mis-timed write cannot be undone.
    [ACTIONS.REVIEWS_VIEW]: ANY,
    [ACTIONS.PRICING_VIEW]: ANY,
    [ACTIONS.BUILDERS_VIEW]: ANY,
    [ACTIONS.BUILDERS_CONFIGURE]: ANY,
    [ACTIONS.PRESENTATIONS_VIEW]: ANY,
    [ACTIONS.TEAMROOMS_VIEW]: ANY,
    [ACTIONS.AITOOLS_VIEW]: ANY,
    // Top Tier Teams is a link directory over surfaces that already exist and
    // are already reachable by any staffer who knows the URL. Both grants stay
    // open to a role-less session for the same reason PRESENTATIONS_VIEW does:
    // the hub is staff-only behind the JWT, the rings console has its own
    // sign-in, and blacking the flagship account's console out for everyone not
    // yet on the roster would be a regression, not security. Note this makes
    // UNSCOPED broader than REP here — deliberate: once real roles land, the
    // sales lane loses the publish console and this permissive fallback stops
    // applying to anyone.
    [ACTIONS.TOPTIER_VIEW]: ANY,
    [ACTIONS.TOPTIER_ADMIN_VIEW]: ANY,
  },
}

/* ---- Resources & the rep's book ------------------------------------- */

const norm = (s) => String(s ?? '').trim().toLowerCase()

/**
 * Accounts reach us in several shapes — a journey deal (`{ id, org, owner }`),
 * a championship record (`{ id, sr_org, sr_assigned_rep }`), or just an id
 * string. One normalizer so `can()` never has to know which module called it.
 */
export function normalizeResource(resource) {
  if (resource == null) return null
  if (typeof resource === 'string' || typeof resource === 'number') {
    return { id: String(resource), name: '', owner: '' }
  }
  if (typeof resource !== 'object') return null
  const pick = (keys) => {
    for (const k of keys) {
      const v = resource[k]
      if (v !== undefined && v !== null && v !== '') return String(v)
    }
    return ''
  }
  return {
    id: pick(['id', 'accountId', 'dealId', 'hs_object_id', 'recordId']),
    name: pick(['org', 'account', 'name', 'dealname', 'sr_org', 'championship']),
    owner: pick([
      'owner',
      'ownerName',
      'assignedRep',
      'sr_assigned_rep',
      'sr_rep_primary',
      'hubspot_owner',
    ]),
  }
}

/** Identity strings that mean "this actor" when matched against an owner field. */
export function actorAliases(user) {
  if (!user || typeof user !== 'object') return []
  const raw = [user.name, user.fullName, user.username, user.email, user.id]
  const out = new Set()
  for (const v of raw) {
    const s = norm(v)
    if (!s) continue
    out.add(s)
    if (s.includes('@')) out.add(s.split('@')[0]) // brian.kass@… → brian.kass
  }
  return [...out]
}

const UNOWNED = new Set(['', 'unassigned', 'unknown', 'none', 'n/a', 'null', 'undefined'])

/**
 * Is this account in the actor's book?
 *
 * Two sources, in order:
 *  1. An EXPLICIT book from the backend (`ctx.book` — a set/array of account
 *     ids). This is the only trustworthy source and the one the backend must
 *     eventually supply.
 *  2. A NAME HEURISTIC — the account's owner field matching the actor's own
 *     name/username/email. Honest fallback so the pattern is demonstrable
 *     today, but it is a guess: two reps named "J. Smith" would collide, and
 *     an unowned account belongs to nobody. Never treat it as authorization.
 */
export function ownsAccount(ctx, resource) {
  const r = normalizeResource(resource)
  if (!r) return false

  const book = ctx?.book
  if (book && (book.size > 0 || book.length > 0)) {
    const has = book instanceof Set ? (k) => book.has(k) : (k) => book.includes(k)
    if (r.id && has(r.id)) return true
    if (r.name && has(r.name)) return true
    return false
  }

  const owner = norm(r.owner)
  if (UNOWNED.has(owner)) return false
  const aliases = ctx?.aliases || []
  return aliases.some((a) => a === owner || (a.length > 2 && owner.includes(a)))
}

/* ---- The check ------------------------------------------------------ */

/**
 * Build the context `can()` evaluates against. `actingAs` is the account the
 * actor has taken control of, if any.
 */
export function makeContext({ user, role, book, actingAs } = {}) {
  const resolved = role ? normalizeRole(role) : roleFromUser(user)
  return {
    user: user || null,
    role: resolved,
    aliases: actorAliases(user),
    book: book || null,
    actingAs: actingAs ? normalizeResource(actingAs) : null,
  }
}

/**
 * The one permission check every module imports.
 *
 *   can(ctx, ACTIONS.JOURNEY_APPROVAL_ADVANCE)        → CAPABILITY question:
 *       "could this actor ever do this?" — use for nav, tabs, showing a button.
 *   can(ctx, ACTIONS.JOURNEY_APPROVAL_ADVANCE, deal)  → AUTHORIZATION question:
 *       "may this actor do this to THIS account?" — use before a write.
 *
 * Three rules, in order:
 *  1. The ACTOR's role decides. Taking control of an account never borrows
 *     that account's (or its owner's) power — impersonation can only ever
 *     narrow what you may do, never widen it.
 *  2. An OWN grant needs the account to be in the actor's book.
 *  3. While acting-as is active, every resource-scoped action is CONTAINED to
 *     the account under control. You cannot take control of one account and
 *     write to another.
 */
export function can(ctx, action, resource) {
  if (!ctx || !action) return false
  const grants = POLICY[ctx.role] || POLICY[ROLES.UNSCOPED]
  const grant = grants[action]
  if (!grant) return false

  // Rule 3 — containment. Checked before ownership so a rep acting as account
  // A cannot reach account B even if B is also in their book.
  if (ctx.actingAs && resource != null) {
    const r = normalizeResource(resource)
    if (!sameAccount(ctx.actingAs, r)) return false
  }

  // Rule 2 — scope.
  if (grant === OWN) {
    if (resource == null) return true // capability question, not authorization
    return ownsAccount(ctx, resource)
  }

  return grant === ANY
}

/** Two normalized resources referring to the same account? */
export function sameAccount(a, b) {
  if (!a || !b) return false
  if (a.id && b.id) return a.id === b.id
  return !!a.name && norm(a.name) === norm(b.name)
}

/**
 * May this actor take control of this account? Thin wrapper over `can()` so
 * callers read as intent, not as policy plumbing.
 */
export function canActAs(ctx, account) {
  return can(ctx, ACTIONS.ACTING_START, account)
}

/** Human-readable reason a check failed — for tooltips and denial notices. */
export function denialReason(ctx, action, resource) {
  if (can(ctx, action, resource)) return ''
  const label = ACTION_META[action]?.label || action
  if (ctx?.role === ROLES.UNSCOPED) {
    return `${label} needs a role on your session. Ask an executive to assign you one in Team Management.`
  }
  const grants = POLICY[ctx?.role] || {}
  if (!grants[action]) {
    return `${ROLE_META[ctx?.role]?.label || 'Your role'} can’t ${label.toLowerCase()}.`
  }
  if (ctx?.actingAs && resource != null) {
    const r = normalizeResource(resource)
    if (!sameAccount(ctx.actingAs, r)) {
      return `You’re acting as ${ctx.actingAs.name || ctx.actingAs.id}. Exit to work on another account.`
    }
  }
  return 'This account isn’t in your book.'
}

/* ---- Nav ------------------------------------------------------------ */

/** Filter a nav list to what this actor may see. Entries with no `action` are public. */
export function visibleNav(ctx, nav) {
  return (nav || []).filter((item) => !item.action || can(ctx, item.action))
}
